import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Tras INSERT en `pagos_credito`, el trigger ya actualizó montos en `facturas`.
 * Esta función solo agrega la entrada detallada en `historial_pagos` (método, notas, origen crédito).
 */
export async function appendHistorialPagoCreditoAFactura(
  supabase: SupabaseClient,
  params: {
    empresaId: string;
    facturaId: string;
    monto: number;
    metodoPago: string;
    notas?: string | null;
    comprobanteUrl?: string | null;
    reciboUrl?: string | null;
    creditoId: string;
    registradoPorNombre: string | null;
    registradoPorId: string | null;
  }
): Promise<{ error: Error | null }> {
  const {
    empresaId,
    facturaId,
    monto,
    metodoPago,
    notas,
    comprobanteUrl,
    reciboUrl,
    creditoId,
    registradoPorNombre,
    registradoPorId,
  } = params;

  const { data: row, error: selErr } = await supabase
    .from('facturas')
    .select('historial_pagos, monto_pagado, monto_pendiente, saldo_pendiente, total')
    .eq('id', facturaId)
    .eq('empresa_id', empresaId)
    .maybeSingle();

  if (selErr) {
    return { error: new Error(selErr.message) };
  }
  if (!row) {
    return { error: new Error('Factura no encontrada') };
  }

  const historialRaw = row.historial_pagos;
  const arr: unknown[] = Array.isArray(historialRaw)
    ? historialRaw
    : typeof historialRaw === 'string'
      ? (() => {
          try {
            const p = JSON.parse(historialRaw);
            return Array.isArray(p) ? p : [];
          } catch {
            return [];
          }
        })()
      : [];

  const total = Number(row.total) || 0;
  const pagado = Number(row.monto_pagado) ?? 0;
  const pendiente =
    row.monto_pendiente != null
      ? Number(row.monto_pendiente)
      : row.saldo_pendiente != null
        ? Number(row.saldo_pendiente)
        : Math.max(0, total - pagado);

  const nuevo: Record<string, unknown> = {
    monto,
    fecha: new Date().toISOString(),
    metodo: metodoPago,
    saldo_despues: Math.max(0, pendiente),
    notas: notas?.trim() || null,
    comprobante_url: comprobanteUrl || null,
    recibo_url: reciboUrl || null,
    origen: 'credito',
    credito_id: creditoId,
    registrado_por: registradoPorNombre,
    registrado_por_id: registradoPorId,
  };

  const update: Record<string, unknown> = {
    historial_pagos: [...arr, nuevo],
    updated_at: new Date().toISOString(),
  };
  if (comprobanteUrl) update.comprobante_url = comprobanteUrl;

  const { error: upErr } = await supabase
    .from('facturas')
    .update(update)
    .eq('id', facturaId)
    .eq('empresa_id', empresaId);

  if (upErr) {
    return { error: new Error(upErr.message) };
  }
  return { error: null };
}
